import { useState } from 'react';
import { useEmployeeMonthlyStatistics } from '@/hooks/useGiftsRequests';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useGetEmployeeRoleIsSeller } from '@/hooks/useEmployee';
import { Button } from '@/components/ui/button';
import { Filter, Users, TrendingUp, Gift, Heart } from 'lucide-react';
import LoadingCircle from '@/components/LoadingCircle';
import ReactECharts from 'echarts-for-react';
import {
  getGroupedBarOption,
  numberFormat,
  currencyFormat,
} from './ChartUtils';

interface EmployeeStatisticsProps {
  selectedYear: number;
  onYearChange: (year: number) => void;
}

const EmployeeStatistics = ({
  selectedYear,
  onYearChange,
}: EmployeeStatisticsProps) => {
  const [employeeId, setEmployeeId] = useState<string | undefined>('');

  // Lấy data filter
  const { data: employeesData } = useGetEmployeeRoleIsSeller(false);

  // Monthly statistics data
  const { data: employeeMonthlyData, isLoading: employeeLoading } =
    useEmployeeMonthlyStatistics(
      employeeId ? Number(employeeId) : undefined,
      selectedYear
    );

  // Xoá lọc
  const handleClearEmployeeFilter = () => {
    setEmployeeId('');
  };

  return (
    <div className="bg-gradient-to-br from-white to-luxury-50/30 rounded-xl shadow-lg border border-luxury-200/50 overflow-hidden w-full">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-4 text-white">
        <div className="flex items-center gap-2">
          <div className="p-1.5 bg-white/20 rounded-lg">
            <Users className="w-5 h-5" />
          </div>
          <div>
            <h2 className="text-lg font-bold">
              Thống kê số lượng quà tặng theo nhân viên
            </h2>
            <p className="text-blue-100 text-xs">
              Phân tích hiệu suất và thành tích
            </p>
          </div>
        </div>
      </div>

      {/* Employee Filter */}
      <div className="p-4 border-b border-luxury-200/50 bg-gradient-to-r from-luxury-50/50 to-white">
        <div className="flex flex-col sm:flex-row flex-wrap items-start sm:items-center gap-3">
          <div className="flex-1 min-w-[200px] w-full sm:w-auto">
            <Select value={employeeId || ''} onValueChange={setEmployeeId}>
              <SelectTrigger className="w-full luxury-input bg-white/80 backdrop-blur-sm border-luxury-300 hover:border-luxury-400 transition-all duration-200 h-9">
                <SelectValue placeholder="Chọn nhân viên" />
              </SelectTrigger>
              <SelectContent className="border-luxury-200 bg-white/95 backdrop-blur-sm">
                {employeesData
                  ?.filter((e) => e.status === 'active')
                  .map((e) => (
                    <SelectItem
                      key={e.id}
                      value={e.id}
                      className="hover:bg-luxury-50"
                    >
                      <span className="font-medium">NV-{e.id}</span> - {e.name}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>
          <div className="min-w-[100px]">
            <Select
              value={selectedYear.toString()}
              onValueChange={(value) => onYearChange(Number(value))}
            >
              <SelectTrigger className="w-full luxury-input bg-white/80 backdrop-blur-sm border-luxury-300 hover:border-luxury-400 transition-all duration-200 h-9">
                <SelectValue placeholder="Năm" />
              </SelectTrigger>
              <SelectContent className="border-luxury-200 bg-white/95 backdrop-blur-sm">
                {Array.from(
                  { length: 5 },
                  (_, i) => new Date().getFullYear() - i
                ).map((year) => (
                  <SelectItem
                    key={year}
                    value={year.toString()}
                    className="hover:bg-luxury-50"
                  >
                    {year}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="luxury-button-outline bg-white/80 backdrop-blur-sm hover:bg-white transition-all duration-200 border-luxury-300 hover:border-luxury-400 h-9"
            onClick={handleClearEmployeeFilter}
          >
            <Filter className="w-3.5 h-3.5 mr-1.5" />
            Xoá lọc
          </Button>
        </div>
      </div>

      {/* Employee Overview */}
      {employeeMonthlyData && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 p-3 sm:p-4">
          <div className="bg-gradient-to-br from-blue-50 to-blue-100/50 rounded-lg p-4 flex flex-col items-center border border-blue-200/50 shadow-md hover:shadow-lg transition-all duration-300 group">
            <div className="p-2 bg-blue-500/10 rounded-full mb-2 group-hover:scale-110 transition-transform duration-200">
              <Gift className="w-5 h-5 text-blue-600" />
            </div>
            <span className="text-gray-600 text-xs font-medium mb-1">
              Tổng số quà
            </span>
            <span className="text-2xl font-bold text-blue-700 group-hover:text-blue-800 transition-colors">
              {numberFormat(employeeMonthlyData.totalGiftsGiven)}
            </span>
          </div>
          <div className="bg-gradient-to-br from-green-50 to-green-100/50 rounded-lg p-4 flex flex-col items-center border border-green-200/50 shadow-md hover:shadow-lg transition-all duration-300 group">
            <div className="p-2 bg-green-500/10 rounded-full mb-2 group-hover:scale-110 transition-transform duration-200">
              <TrendingUp className="w-5 h-5 text-green-600" />
            </div>
            <span className="text-gray-600 text-xs font-medium mb-1">
              Tổng giá trị
            </span>
            <span className="text-2xl font-bold text-green-600 group-hover:text-green-700 transition-colors">
              {currencyFormat(employeeMonthlyData.totalValue)}
            </span>
          </div>
          <div className="bg-gradient-to-br from-purple-50 to-purple-100/50 rounded-lg p-4 flex flex-col items-center border border-purple-200/50 shadow-md hover:shadow-lg transition-all duration-300 group">
            <div className="p-2 bg-purple-500/10 rounded-full mb-2 group-hover:scale-110 transition-transform duration-200">
              <Heart className="w-5 h-5 text-purple-600" />
            </div>
            <span className="text-gray-600 text-xs font-medium mb-1">
              Số yêu cầu
            </span>
            <span className="text-2xl font-bold text-purple-700 group-hover:text-purple-800 transition-colors">
              {numberFormat(employeeMonthlyData.totalRequests)}
            </span>
          </div>
        </div>
      )}

      {/* Employee Chart */}
      <div className="p-3 sm:p-4">
        {employeeLoading ? (
          <div className="flex items-center justify-center h-64">
            <LoadingCircle />
          </div>
        ) : !employeeId ? (
          <div className="flex flex-col items-center justify-center h-64 text-gray-500">
            <div className="p-3 bg-luxury-50 rounded-full mb-3">
              <Users className="w-6 h-6 text-luxury-400" />
            </div>
            <p className="text-base font-medium">
              Vui lòng chọn nhân viên để xem thống kê
            </p>
            <p className="text-xs text-gray-400 mt-1">
              Chọn một nhân viên từ danh sách trên để hiển thị biểu đồ
            </p>
          </div>
        ) : employeeMonthlyData ? (
          <div className="bg-white/50 backdrop-blur-sm rounded-lg p-3 border border-luxury-200/50">
            <div className="h-[320px]">
              <ReactECharts
                option={getGroupedBarOption(employeeMonthlyData.monthlyData)}
                style={{ height: 320 }}
                notMerge={true}
                lazyUpdate={true}
              />
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center h-64 text-red-500">
            <div className="p-3 bg-red-50 rounded-full mb-3">
              <Users className="w-6 h-6 text-red-400" />
            </div>
            <p className="text-base font-medium">Không có dữ liệu</p>
            <p className="text-xs text-red-400 mt-1">
              Không tìm thấy dữ liệu thống kê cho nhân viên này
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default EmployeeStatistics;
